<div class="create-category-page">

  <!-- ── Header ──────────────────────────────────────────────────��──────── -->
  <header class="page-header">
    <div>
      <h1>New Service Category</h1>
      <p>Define a category, LLM description, and intake questions.</p>
    </div>
    <a class="primary-teal-btn" [routerLink]="['/service-categories']">
      <mat-icon aria-hidden="true">arrow_back</mat-icon>
      Back
    </a>
  </header>

  <!-- ── Success ────────────────────────────────────────────────────────── -->
  @if (saved()) {
    <div class="success-card">
      <div class="success-icon-wrap">
        <mat-icon>check_circle</mat-icon>
      </div>
      <div class="success-body">
        <strong>Service category created.</strong>
        <p>Category ID: <code>{{ savedId() }}</code></p>
      </div>
      <div class="success-actions">
        <button class="form-cancel-btn" (click)="addAnother()">Add Another</button>
        <a class="primary-blue-btn" [routerLink]="['/service-categories', savedId()]">Open Category</a>
        <a class="primary-blue-btn" [routerLink]="['/service-categories']">Go to List</a>
      </div>
    </div>

  } @else {

    <!-- ── Form card ──────────────────────────────────────────────────────── -->
    <div class="form-card">
      <form [formGroup]="form" (ngSubmit)="submit()" novalidate class="form-body">

        <div class="form-scroll">
          <div class="form-cols">

            <!-- ════ LEFT COLUMN ════ -->
            <div class="form-col">
              <h2 class="col-title">Identity</h2>

              <!-- Name -->
              <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                <mat-label>Category name *</mat-label>
                <input matInput formControlName="name" placeholder="e.g. Home Plumbing" maxlength="200" />
                @if (form.controls.name.touched && form.controls.name.hasError('required')) {
                  <mat-error>Name is required.</mat-error>
                }
              </mat-form-field>

              <!-- Vertical -->
              <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                <mat-label>Vertical *</mat-label>
                <mat-select formControlName="vertical">
                  @for (opt of VERTICAL_OPTIONS; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                  }
                </mat-select>
                @if (form.controls.vertical.touched && form.controls.vertical.hasError('required')) {
                  <mat-error>Vertical is required.</mat-error>
                }
              </mat-form-field>

              <!-- Category ID (auto-filled) -->
              <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                <mat-label>Category ID (slug)</mat-label>
                <input matInput formControlName="category_id" [placeholder]="autoSlug() || 'auto-generated'" maxlength="100" />
                <mat-hint>Leave blank to auto-generate from name + vertical</mat-hint>
              </mat-form-field>

              <!-- Description AI -->
              <mat-form-field appearance="outline" floatLabel="always" class="full-width notes-field">
                <mat-label>LLM Description *</mat-label>
                <textarea
                  matInput
                  formControlName="description_ai"
                  rows="6"
                  maxlength="3000"
                  placeholder="Describe what service requests belong to this category. Injected into LLM prompts."
                ></textarea>
                <mat-hint>Used by the AI for intent classification · Max 3 000 chars</mat-hint>
                @if (form.controls.description_ai.touched && form.controls.description_ai.hasError('required')) {
                  <mat-error>Description is required.</mat-error>
                }
              </mat-form-field>

              <!-- is_active toggle -->
              <div class="toggle-card">
                <div class="toggle-card-body">
                  <div class="toggle-card-text">
                    <span>Active</span>
                    <small>Inactive categories are excluded from LLM matching and operator views</small>
                  </div>
                  <mat-slide-toggle formControlName="is_active" color="primary"></mat-slide-toggle>
                </div>
              </div>

            </div>
            <!-- end left col -->

            <!-- ════ RIGHT COLUMN ════ -->
            <div class="form-col">
              <h2 class="col-title">LLM Intake</h2>

              <!-- Example Requests -->
              <div class="chip-field">
                <span class="chip-field-label">Example Requests</span>
                <div class="chip-input-row">
                  <mat-form-field appearance="outline" floatLabel="always" subscriptSizing="dynamic" class="full-width chip-input-field">
                    <mat-label>Add example</mat-label>
                    <input
                      matInput
                      [value]="exampleInput()"
                      (input)="exampleInput.set($any($event.target).value)"
                      (keydown)="onExampleKeydown($event)"
                      placeholder="e.g. my sink is leaking"
                      maxlength="300"
                    />
                  </mat-form-field>
                  <button
                    type="button"
                    class="chip-add-btn"
                    [disabled]="!exampleInput().trim()"
                    (click)="addExample()"
                    matTooltip="Add (Enter)"
                  >
                    <mat-icon>add</mat-icon>
                  </button>
                </div>
                <div class="chip-list">
                  @if (form.controls.example_requests.value.length === 0) {
                    <span class="chip-empty">No examples added yet.</span>
                  }
                  @for (item of form.controls.example_requests.value; track item; let i = $index) {
                    <span class="chip">
                      {{ item }}
                      <button type="button" class="chip-remove" (click)="removeExample(i)">
                        <mat-icon>close</mat-icon>
                      </button>
                    </span>
                  }
                </div>
                <p class="field-hint">Shown to the LLM as few-shot examples for category identification.</p>
              </div>

              <!-- Confirmation Questions -->
              <div class="chip-field">
                <span class="chip-field-label">Confirmation Questions</span>
                <div class="chip-input-row">
                  <mat-form-field appearance="outline" floatLabel="always" subscriptSizing="dynamic" class="full-width chip-input-field">
                    <mat-label>Add question</mat-label>
                    <input
                      matInput
                      [value]="confirmInput()"
                      (input)="confirmInput.set($any($event.target).value)"
                      (keydown)="onConfirmKeydown($event)"
                      placeholder="e.g. Which area is affected?"
                      maxlength="300"
                    />
                  </mat-form-field>
                  <button
                    type="button"
                    class="chip-add-btn"
                    [disabled]="!confirmInput().trim()"
                    (click)="addConfirm()"
                    matTooltip="Add (Enter)"
                  >
                    <mat-icon>add</mat-icon>
                  </button>
                </div>
                <div class="chip-list">
                  @if (form.controls.confirmation_questions.value.length === 0) {
                    <span class="chip-empty">No questions added yet.</span>
                  }
                  @for (item of form.controls.confirmation_questions.value; track item; let i = $index) {
                    <span class="chip">
                      {{ item }}
                      <button type="button" class="chip-remove" (click)="removeConfirm(i)">
                        <mat-icon>close</mat-icon>
                      </button>
                    </span>
                  }
                </div>
                <p class="field-hint">Asked by the AI after the category is identified, before handoff.</p>
              </div>

              <!-- Service Models -->
              <div class="chip-field">
                <span class="chip-field-label">
                  Service Models *
                  <mat-icon
                    class="help-icon"
                    matTooltip="Select at least one dispatch model for this category."
                    matTooltipPosition="right"
                  >info_outline</mat-icon>
                </span>
                <div class="model-options">
                  @for (opt of SERVICE_MODEL_OPTIONS; track opt.value) {
                    <label class="model-option">
                      <input
                        type="checkbox"
                        class="model-checkbox"
                        [checked]="isModelSelected(opt.value)"
                        (change)="toggleModel(opt.value)"
                      />
                      <div class="model-option-text">
                        <strong>{{ opt.label }}</strong>
                        <small>{{ opt.hint }}</small>
                      </div>
                    </label>
                  }
                </div>
                @if (form.controls.service_models.touched && form.controls.service_models.hasError('required')) {
                  <span class="field-error">At least one service model is required.</span>
                }
              </div>

            </div>
            <!-- end right col -->

          </div>

          @if (error()) {
            <div class="error-banner">
              <mat-icon>error_outline</mat-icon>
              {{ error() }}
            </div>
          }
        </div>
        <!-- end form-scroll -->

        <!-- ── Footer actions ─────────────────────────────────────────────── -->
        <div class="form-actions">
          <div class="form-actions-left">
            <a class="form-cancel-btn" [routerLink]="['/service-categories']">Cancel</a>
          </div>
          <div class="form-actions-right">
            <button
              type="submit"
              class="primary-green-btn"
              [disabled]="formInvalid() || saving()"
            >
              @if (saving()) {
                <mat-progress-spinner mode="indeterminate" diameter="18"></mat-progress-spinner>
                Saving…
              } @else {
                Create Category
              }
            </button>
          </div>
        </div>

      </form>
    </div>

  }

</div>